import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { publicApi } from '@/lib/api';

const empty = { name: '', email: '', phone: '', subject: '', message: '' };

const ContactForm = () => {
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Please fill in name, email and message.');
            return;
        }
        setSubmitting(true);
        try {
            await publicApi.submitContact(form);
            setDone(true);
            setForm(empty);
            toast.success('Message sent. We will get back to you soon.');
        } catch (err) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="text-center py-8" data-testid="contact-form-success">
                <div className="h-14 w-14 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] mx-auto flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-7 w-7" />
                </div>
                <div className="font-display font-semibold text-xl mb-1">Message received</div>
                <p className="text-sm text-muted-foreground">
                    We typically respond within 1-2 business days.
                </p>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setDone(false)}
                    data-testid="contact-form-reset"
                >
                    Send another message
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="c-name">Name *</Label>
                    <Input
                        id="c-name"
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        required
                        data-testid="contact-input-name"
                    />
                </div>
                <div>
                    <Label htmlFor="c-email">Email *</Label>
                    <Input
                        id="c-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        required
                        data-testid="contact-input-email"
                    />
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="c-phone">Phone</Label>
                    <Input
                        id="c-phone"
                        value={form.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        data-testid="contact-input-phone"
                    />
                </div>
                <div>
                    <Label htmlFor="c-subject">Subject</Label>
                    <Input
                        id="c-subject"
                        value={form.subject}
                        onChange={(e) => update('subject', e.target.value)}
                        placeholder="How can we help?"
                        data-testid="contact-input-subject"
                    />
                </div>
            </div>
            <div>
                <Label htmlFor="c-message">Message *</Label>
                <Textarea
                    id="c-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder="Your message..."
                    required
                    data-testid="contact-input-message"
                />
            </div>
            <Button
                type="submit"
                disabled={submitting}
                className="bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                data-testid="contact-form-submit-button"
            >
                {submitting ? (
                    <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                    </>
                ) : (
                    <>
                        <Send className="h-4 w-4 mr-2" /> Send Message
                    </>
                )}
            </Button>
        </form>
    );
};

export default ContactForm;
